import React, { useEffect, useState, useCallback } from "react";
import Navbar from "./Navbar";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// ─── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, visible }) {
  return (
    <div className={`mc-toast ${visible ? "mc-toast--show" : ""}`} role="status" aria-live="polite">
      <span className="mc-toast__icon">✓</span>
      {message}
    </div>
  );
}

// ─── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="mc-card mc-card--skeleton">
      <div className="mc-card__photo-wrap mc-skeleton-block" />
      <div className="mc-card__body">
        <div className="mc-skeleton-line mc-skeleton-line--wide" />
        <div className="mc-skeleton-line mc-skeleton-line--narrow" />
        <div className="mc-skeleton-chips">
          <div className="mc-skeleton-chip" />
          <div className="mc-skeleton-chip" />
          <div className="mc-skeleton-chip" />
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="mc-empty">
      <div className="mc-empty__icon">💍</div>
      <h2 className="mc-empty__title">No matches yet</h2>
      <p className="mc-empty__body">
        We're working on finding your best matches. Complete your profile to improve your compatibility score.
      </p>
      <a href="/my-account" className="mc-btn mc-btn--primary">Complete profile</a>
    </div>
  );
}

// ─── Compatibility bar ─────────────────────────────────────────────────────────
function CompatBar({ score }) {
  const pct = Math.min(100, Math.max(0, score));
  const label = pct >= 80 ? "Excellent match" : pct >= 60 ? "Strong match" : pct >= 40 ? "Good match" : "Potential match";
  return (
    <div className="mc-compat">
      <div className="mc-compat__header">
        <span className="mc-compat__label">{label}</span>
        <span className="mc-compat__pct">{pct}%</span>
      </div>
      <div className="mc-compat__track">
        <div className="mc-compat__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Reveal section ────────────────────────────────────────────────────────────
function RevealSection({ isRevealed, email, onReveal, onCopy }) {
  return (
    <div className="mc-reveal">
      {isRevealed ? (
        <div className="mc-reveal__email">
          <span className="mc-reveal__email-icon">✉</span>
          <span className="mc-reveal__email-text">{email}</span>
          <button className="mc-reveal__copy" onClick={onCopy} aria-label="Copy email">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
        </div>
      ) : (
        <button className="mc-btn mc-btn--reveal" onClick={onReveal}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Reveal contact
        </button>
      )}
    </div>
  );
}

// ─── Match card ────────────────────────────────────────────────────────────────
function MatchCard({ user, isRevealed, onReveal, onCopy, animDelay, compatScore }) {
  const photos = Array.isArray(user.photos) && user.photos.length
    ? user.photos
    : [user.photoURL || "https://cdn-icons-png.flaticon.com/512/149/149071.png"];

  return (
    <article className="mc-card" style={{ animationDelay: `${animDelay}ms` }}>
      <div className="mc-card__photo-wrap">
        <Swiper modules={[Navigation, Pagination]} navigation pagination={{ clickable: true }} className="mc-swiper">
          {photos.map((url, i) => (
            <SwiperSlide key={i}>
              <img src={url} alt={`${user.name} photo ${i + 1}`} className="mc-card__photo" loading="lazy" />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="mc-card__age-badge">{user.age}</div>
      </div>

      <div className="mc-card__body">
        <div className="mc-card__header">
          <div>
            <h2 className="mc-card__name">{user.name}</h2>
            <div className="mc-card__meta">
              {user.location && (
                <span className="mc-card__meta-item">
                  <svg width="11" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {user.location}
                </span>
              )}
              {user.occupation && (
                <span className="mc-card__meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  {user.occupation}
                </span>
              )}
            </div>
          </div>
        </div>

        <CompatBar score={compatScore} />

        {user.personalityTraits?.length > 0 && (
          <div className="mc-card__traits">
            {user.personalityTraits.slice(0, 4).map((t, i) => (
              <span key={i} className="mc-chip">{t}</span>
            ))}
            {user.personalityTraits.length > 4 && (
              <span className="mc-chip mc-chip--more">+{user.personalityTraits.length - 4}</span>
            )}
          </div>
        )}

        {user.sharedHobbies?.length > 0 && (
          <p className="mc-card__shared">
            <span className="mc-card__shared-icon">✦</span>
            You both enjoy: {user.sharedHobbies.slice(0, 3).join(", ")}
          </p>
        )}

        <RevealSection isRevealed={isRevealed} email={user.email} onReveal={onReveal} onCopy={onCopy} />
      </div>
    </article>
  );
}

// ─── main ──────────────────────────────────────────────────────────────────────
export default function Matches() {
  const [matches,        setMatches]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [revealedEmails, setRevealedEmails] = useState({});
  const [toast,          setToast]          = useState({ visible: false, message: "" });
  const [currentUser,    setCurrentUser]    = useState(null);
  const auth = getAuth();

  const showToast = useCallback((message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2500);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setLoading(false); return; }
      setCurrentUser(user);
      try {
        const q = query(collection(db, "matches"), where("users", "array-contains", user.uid));
        const snapshot = await getDocs(q);
        const uniqueUsers = new Map();
        for (const matchDoc of snapshot.docs) {
          const { users } = matchDoc.data();
          const otherId = users.find(id => id !== user.uid);
          if (otherId && !uniqueUsers.has(otherId)) {
            const userDoc = await getDoc(doc(db, "users", otherId));
            if (userDoc.exists()) uniqueUsers.set(otherId, { id: otherId, ...userDoc.data() });
          }
        }
        setMatches(Array.from(uniqueUsers.values()));
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [auth]);

  const toggleReveal = (userId) =>
    setRevealedEmails(prev => ({ ...prev, [userId]: !prev[userId] }));

  const copyEmail = (email) =>
    navigator.clipboard.writeText(email).then(() => showToast("Email copied to clipboard"));

  const calcCompat = (user) => {
    const fields = ["personalityTraits", "hobbies", "longTermGoals", "lifestyleChoices", "culturalBackground"];
    const shared = fields.reduce((acc, f) => {
      const a = currentUser?.[f] || [];
      const b = user[f] || [];
      return acc + a.filter(v => b.includes(v)).length;
    }, 0);
    return Math.min(98, 45 + shared * 6);
  };

  return (
    <div className="mc-root">
      <Navbar />
      <Toast message={toast.message} visible={toast.visible} />

      <div className="mc-page">
        <header className="mc-header">
          <div className="mc-header__inner">
            <div>
              <h1 className="mc-title">Your Matches</h1>
              <p className="mc-subtitle">
                {loading ? "Finding your matches…" :
                 matches.length === 0 ? "No matches yet" :
                 `${matches.length} compatible ${matches.length === 1 ? "profile" : "profiles"} found`}
              </p>
            </div>
            {!loading && matches.length > 0 && (
              <div className="mc-header__badge">
                <span className="mc-header__badge-icon">💍</span>
                <span className="mc-header__badge-text">
                  {matches.filter(m => revealedEmails[m.id]).length} contacted
                </span>
              </div>
            )}
          </div>
        </header>

        <main className="mc-container">
          {loading ? (
            <div className="mc-grid">{[1,2,3].map(i => <SkeletonCard key={i} />)}</div>
          ) : matches.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="mc-grid">
              {matches.map((user, idx) => (
                <MatchCard
                  key={user.id} user={user}
                  isRevealed={!!revealedEmails[user.id]}
                  onReveal={() => toggleReveal(user.id)}
                  onCopy={() => copyEmail(user.email)}
                  animDelay={idx * 80}
                  compatScore={calcCompat(user)}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}